/**
 * Pure colour-expression helpers shared by the Esri renderer code. Kept free of
 * `@arcgis/core` / DOM imports so the colour-band and icon-image transforms are
 * unit-testable in isolation (and reusable from `rendererHelpers`, `useTheme`,
 * and `ReactEsriMap`).
 */

/**
 * Converts a colour range into half-open numeric bands `[min, max)`. Each band
 * starts at its own value and ends at the next band's value; the **top band is
 * open-ended** (`Number.MAX_SAFE_INTEGER`) so an "≥ N → colour" rule matches all
 * values above N rather than only the single point N (which a zero-width
 * `[N, N]` band would). Mirrors the VTL `step` logic in
 * `useLayers.getTileColorExpression`. Shared by the FeatureLayer/GeoJSON
 * `ClassBreaksRenderer` (useTheme) and the editor bridge (ReactEsriMap).
 */
export function colorRangeToBands(
	colorRange: ReadonlyArray<{ value: number; color: string }>,
): { min: number; max: number; color: string }[] {
	return colorRange.map(({ value, color }, i, arr) => ({
		min: Number(value),
		max: Number(arr[i + 1]?.value ?? Number.MAX_SAFE_INTEGER),
		color,
	}));
}

/**
 * Walks a Mapbox `icon-image` expression (string or nested array) and replaces
 * the *first* `~#rrggbb` color segment with `newColor`, preserving any
 * subsequent suffixes (e.g. `-selected`, `-nobackground`).
 */
export function replaceColorInIconImageExpr(
	expr: unknown,
	newColor: string,
): unknown {
	if (typeof expr === 'string') {
		const tildeIdx = expr.indexOf('~');
		if (tildeIdx < 0) return expr;
		const after = expr.slice(tildeIdx + 1);
		if (!after.startsWith('#')) return expr;
		// Stop at the first non-hex character to find the end of the hex color.
		const colorBodyEnd = after.slice(1).search(/[^a-f0-9]/i);
		const rest = colorBodyEnd < 0 ? '' : after.slice(colorBodyEnd + 1);
		const ensured = newColor.startsWith('#') ? newColor : `#${newColor}`;
		return `${expr.slice(0, tildeIdx)}~${ensured}${rest}`;
	}
	if (!Array.isArray(expr)) return expr;
	return expr.map(item => replaceColorInIconImageExpr(item, newColor));
}

/**
 * Converts a Mapbox paint *colour* expression (as produced by
 * `getTileColorExpression` — a `match`/`step` keyed on the feature, whose
 * outputs are colour strings) into an equivalent `icon-image` expression whose
 * outputs are recoloured sprite ids derived from `baseSprite`. Structure
 * (`match`/`step` shape, feature key, range stops) is preserved; only the colour
 * leaves are mapped through {@link replaceColorInIconImageExpr}.
 */
export function colorExpressionToIconImageExpression(
	colorExpr: unknown,
	baseSprite: string,
): unknown {
	// Colour leaf → recoloured sprite id.
	if (typeof colorExpr === 'string') {
		return replaceColorInIconImageExpr(baseSprite, colorExpr);
	}
	if (!Array.isArray(colorExpr)) return colorExpr;
	const head = colorExpr[0];
	if (head === 'match') {
		// ['match', key, label, out, label, out, ..., fallback]
		const out: unknown[] = ['match', colorExpr[1]];
		for (let i = 2; i < colorExpr.length - 1; i += 2) {
			out.push(
				colorExpr[i],
				colorExpressionToIconImageExpression(
					colorExpr[i + 1],
					baseSprite,
				),
			);
		}
		out.push(
			colorExpressionToIconImageExpression(
				colorExpr[colorExpr.length - 1],
				baseSprite,
			),
		);
		return out;
	}
	if (head === 'step') {
		// ['step', input, out0, stop1, out1, stop2, out2, ...]
		const out: unknown[] = [
			'step',
			colorExpr[1],
			colorExpressionToIconImageExpression(colorExpr[2], baseSprite),
		];
		for (let i = 3; i < colorExpr.length; i += 2) {
			out.push(
				colorExpr[i],
				colorExpressionToIconImageExpression(
					colorExpr[i + 1],
					baseSprite,
				),
			);
		}
		return out;
	}
	return colorExpr;
}
